/**
 * ProjectsEmptyBanner.tsx — WEB-518: explicit empty-state replacing the
 * silent auto-create of "My Project" that used to run inside
 * fetchProjectsThunk whenever an org had zero projects.
 *
 * Renders nothing while the project list is loading or non-empty. When the
 * active org genuinely has zero projects, shows an actionable banner.
 *
 * WEB-3739 доводка 1 — вид по эталону `design/cabinet-rescin/app.jsx` (стр. 724,
 * стили `.noproj` стр. 56): свой корень `.ds` с темой человека (плашка стоит в
 * `main` вне корня оболочки, как плашка заморозки `.frz-shell`), в нём `Notice`
 * кита вида info (`noproj-banner`): заголовок «У вас пока нет проектов», текст
 * «Создайте первый проект, чтобы начать собирать воронки и квизы.», действием —
 * `Button kind="primary"` «Создать проект» (метка кода `create-first-project-btn`).
 * Значка-подсказки нет (в эталоне его нет), antd из файла ушёл. Без права
 * кнопка погашена, причина — в подсказке кнопки (`data-tip`, приём экрана
 * «Проекты» и эталона `org.jsx:542`).
 *
 * On successful creation: refreshes the project list, sets the new project
 * as current and navigates to the project landing `PROJECT_LANDING` (`/home`,
 * решение владельца п. 99; раньше — `/funnels`).
 *
 * WEB-1556: это ВТОРАЯ дверь к тому же мастеру создания (первая — экран
 * «Проекты» организации). Баннер живёт в каркасе кабинета
 * (router/DashboardLayout.tsx) и показывается всем, у кого список проектов
 * пуст, — включая тех, кому сервер создавать проекты не даёт. Право считается
 * тем же общим источником роли в ОРГАНИЗАЦИИ (useOrgRole), что и на экране
 * «Проекты», текст отказа — общий (constants/projectCreateRights.ts).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Notice } from '@/components/kit/ds';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import {
  fetchProjectsThunk,
  setCurrentProject,
  switchActiveProjectThunk,
} from '@/store/projectsSlice';
import { CreateProjectWizard } from '@/components/context/CreateProjectWizard';
import { useOrgRole } from '@/hooks/useOrgRole';
import { createProjectDeniedHint } from '@/constants/projectCreateRights';
import { PROJECT_LANDING } from '@/router/landingRoute';
import { useStrings } from '@/i18n';
import { projectsStrings } from '@/i18n/dictionaries/projects';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import type { Project } from '@/types';
import '../../styles/cabinet-shell.css';

export function ProjectsEmptyBanner() {
  const dispatch = useAppDispatch();
  const s = useStrings(projectsStrings);
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth?.user);
  const root = useCabinetRootAttributes(user?.theme);
  const { projects, isLoading } = useAppSelector((state) => state.projects);
  const activeOrgId = useAppSelector((state) => state.organizations.activeOrgId);
  // WEB-1928 (в), шаг 9: активную организацию только что поправил кабинет
  // (её не было в списке доступных), а список проектов в сторе — ещё старый,
  // уехавший с негодным идентификатором и вернувшийся пустым. Показать в этот
  // момент «У вас пока нет проектов» — соврать: проекты у человека есть, их
  // просто спрашивали не у той организации. Метка снимается в тот же миг,
  // когда реакция запускает перезапрос (hooks/useActiveOrgSync.ts).
  const orgAutoCorrectionPending = useAppSelector(
    (state) => Boolean(state.organizations.autoCorrectedOrgId),
  );
  // Право «создать проект» = роль владельца или администратора ОРГАНИЗАЦИИ
  // (ровно то, что спрашивает сервер). Fail-closed: пока роль не известна —
  // кнопка погашена; законный владелец мигания не увидит, баннер рисуется
  // внутри OrgBootstrapGate, уже после загрузки списка организаций.
  const { isOrgAdmin: canCreate } = useOrgRole();
  const [wizardOpen, setWizardOpen] = useState(false);

  if (isLoading || orgAutoCorrectionPending || projects.length > 0) {
    return null;
  }

  const handleWizardClose = (project?: Project) => {
    setWizardOpen(false);
    if (!project) return;

    dispatch(fetchProjectsThunk(activeOrgId ?? undefined));
    dispatch(setCurrentProject(project));
    dispatch(switchActiveProjectThunk(project.id));
    navigate(PROJECT_LANDING);
  };

  const create = (
    <Button
      kind="primary"
      disabled={!canCreate}
      data-testid="create-first-project-btn"
      onClick={() => setWizardOpen(true)}
    >
      {s.createProject}
    </Button>
  );

  return (
    <div
      {...root}
      className={`${root.className} noproj`}
      data-testid="projects-empty-banner"
    >
      <Notice
        kind="info"
        testid="noproj-banner"
        title={s.projectsEmptyBannerTitle}
        action={
          // Кнопку не прячем, а гасим: исчезнувшая кнопка не объясняет человеку,
          // почему ему нельзя, — причина в подсказке кнопки (у погашенной кнопки
          // фокуса нет, поэтому подсказку держит обёртка с tabIndex).
          canCreate ? (
            create
          ) : (
            <span data-tip={createProjectDeniedHint()} tabIndex={0}>
              {create}
            </span>
          )
        }
      >
        {s.projectsEmptyBannerTextShort}
      </Notice>

      <CreateProjectWizard open={wizardOpen} onClose={handleWizardClose} />
    </div>
  );
}
